import { Button, Card, DatePicker, Form, Grid, Input, Message, Select, Space } from "@arco-design/web-react"
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { InputNumber } from "@/components/Numeric";
import { WOPOAPI, WorkOrderAPI, BOMAPI } from "@/apis/manufacturing";
import _ from "lodash";
import Decimal from "decimal.js";
import WarehouseSelect from "@/components/WarehouseSelect";
import InventorySelect from "@/components/InventorySelect";
import { formatDate } from "@/utils";

const FormItem = Form.Item;
const Row = Grid.Row;
const Col = Grid.Col;

export const WoPickOrderEdit = () => {
    const navigate = useNavigate();
    const params = useParams();
    const [form] = Form.useForm();
    const [woRawItems, setWoRawItems] = useState([]);
    const [items, setItems] = useState();
    const [pickQtyTotal, setPickQtyTotal] = useState(0);
    const [wasteFactor, setWasteFactor] = useState(0);
    const [bomRawItems, setBomRawItems] = useState([]);
    const [warehouseId, setWarehouseId] = useState();

    const distributePickQty = (totalQty) => {
        const formItems = form.getFieldValue("items") || [];
        if (!formItems.length || !totalQty) return;

        const dTotalQty = new Decimal(totalQty);
        const wasteMultiplier = new Decimal(wasteFactor || 0).div(100).plus(1);
        const updatedItems = formItems.map((item) => {
            const bomQty = new Decimal(item?.bomQty || 0);
            const allocated = dTotalQty.times(bomQty).times(wasteMultiplier).toDecimalPlaces(4, Decimal.ROUND_HALF_UP);
            return { ...item, pickQty: allocated.toNumber() };
        });
        form.setFieldValue("items", updatedItems);
    };

    const onWarehouseChange = async (val) => {
        setWarehouseId(val);
        const woId = form.getFieldValue("workOrderId");
        let estimateValues;
        try {
            estimateValues = await WOPOAPI.estimate(woId, val);
        } catch (e) {
            Message.error(e.message);
            return;
        }

        // Build a map of estimated items by itemId for quick lookup
        const estimateMap = _.keyBy(estimateValues || [], "itemId");

        // Create items list from ALL woRawItems, merging with estimate data where available
        let allItems = woRawItems.map((rawItem) => {
            const estimated = estimateMap[rawItem.itemId];
            const bomRawItem = _.find(bomRawItems, (ri) => ri.itemId === rawItem.itemId);
            const bomQty = bomRawItem ? bomRawItem.quantity : 0;

            if (estimated) {
                // Item has inventory - use estimated data
                return {
                    ...estimated,
                    itemNo: rawItem.itemNo || estimated.itemNo,
                    itemName: rawItem.itemName,
                    woRawItemId: rawItem.id,
                    bomQty,
                    inventoryKey: (estimated.warehouseId || estimated.locationId || estimated.lot) ? {
                        warehouseId: estimated.warehouseId,
                        locationId: estimated.locationId,
                        lot: estimated.lot
                    } : null
                };
            } else {
                // Item has NO inventory in this warehouse - still show it
                return {
                    itemId: rawItem.itemId,
                    itemNo: rawItem.itemNo,
                    itemName: rawItem.itemName,
                    description: rawItem.description,
                    uom: rawItem.uom,
                    woRawItemId: rawItem.id,
                    bomQty,
                    pickQty: null,
                    warehouseId: null,
                    locationId: null,
                    locationName: null,
                    lot: null,
                    expiredDate: null,
                    inventoryKey: null
                };
            }
        });

        allItems = _.sortBy(allItems, "itemName");
        form.setFieldValue("items", allItems);
        form.setFieldValue("warehouseId", val);
        distributePickQty(pickQtyTotal);
    }

    const onLotSelect = (field, lot) => {
        form.setFieldValue(`${field}.locationId`, lot.locationId);
        form.setFieldValue(`${field}.locationName`, lot.locationName);
        form.setFieldValue(`${field}.lot`, lot.lot);
        form.setFieldValue(`${field}.expiredDate`, lot.expiredDate ? formatDate(lot.expiredDate) : null);
    }

    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (saving) return;
        setSaving(true);
        try {
            const values = await form.validate();

            if (!values.warehouseId) {
                Message.warning("Please select a warehouse");
                return;
            }
            if (!values.pickDate) {
                Message.warning("Please select a pick date");
                return;
            }
            if (!pickQtyTotal || pickQtyTotal <= 0) {
                Message.warning("Please enter a pick quantity");
                return;
            }
            if (!Number.isInteger(pickQtyTotal) || pickQtyTotal <= 0) {
                Message.warning("Pick quantity must be a positive whole number");
                return;
            }

            const formItems = values.items || [];
            const hasEmptyPickQty = formItems.some(item => !item.pickQty || item.pickQty <= 0);
            if (hasEmptyPickQty) {
                Message.warning("All BOM raw items must be picked. Please ensure every item has a pick quantity.");
                return;
            }

            const hasNoInventory = formItems.some(item => item.pickQty > 0 && !item.inventoryKey);
            if (hasNoInventory) {
                Message.warning("Insufficient inventory. Please select inventory location for all items.");
                return;
            }

            const data = {
                id: values.id,
                workOrderId: values.workOrderId,
                warehouseId: values.warehouseId,
                pickDate: values.pickDate,
                pickQty: pickQtyTotal,
                notes: values.notes,
                items: _.map(values.items, (item) => _.omit(item, ['inventoryKey', 'bomQty']))
            };
            await WOPOAPI.update(data.id, data);
            Message.success("Pick order updated successfully");
            navigate(`/workspace/manufacturing/wo-pick-order/${params.poId}`);
        } catch (error) {
            Message.error(error.message);
        } finally {
            setSaving(false);
        }
    }

    useEffect(() => {
        const init = async () => {
            const wopoInfo = await WOPOAPI.get(params.poId);
            const woInfo = await WorkOrderAPI.get(wopoInfo.workOrderId);

            setWasteFactor(woInfo.wasteFactor || 0);

            let bomItems = [];
            if (woInfo.bomId) {
                const bom = await BOMAPI.get(woInfo.bomId);
                bomItems = bom.rawItems || [];
                setBomRawItems(bomItems);
            }

            _.forEach(wopoInfo.items, (item) => {
                const bomRawItem = _.find(bomItems, (ri) => ri.itemId === item.itemId);
                item.bomQty = bomRawItem ? bomRawItem.quantity : 0;
                if (item.warehouseId || item.locationId || item.lot) {
                    item.inventoryKey = {
                        warehouseId: item.warehouseId,
                        locationId: item.locationId,
                        lot: item.lot
                    };
                }
            });

            let sortedItems = _.sortBy(woInfo.rawItems, "itemName");
            setItems(_.map(sortedItems, (item) => ({
                label: <span>{item.itemName}</span>,
                value: item.id
            })));
            setWoRawItems(woInfo.rawItems);

            form.setFieldsValue({
                ...wopoInfo,
                woQuantity: woInfo.quantity,
            });
            setPickQtyTotal(wopoInfo.pickQty || 0);
            setWarehouseId(wopoInfo.warehouseId);
        };
        init();
    }, [params, form])

    return (
        <Card title="Edit WO Pick Order" extra={
            <Space>
                <Button type="primary" loading={saving} onClick={() => save()}>Save</Button>
                <Button onClick={() => navigate(`/workspace/manufacturing/wo-pick-order/${params.poId}`)}>Cancel</Button>
            </Space>
        }>
            <Form form={form} colon
                labelCol={{ span: 0 }}
                wrapperCol={{ span: 24 }}>
                <FormItem field="id" hidden>
                    <Input />
                </FormItem>
                <FormItem field="workOrderId" hidden>
                    <Input />
                </FormItem>
                <FormItem>
                    <Row gutter={8}>
                        <Col span={4}>
                            <FormItem label="WO#" field="workOrderNo">
                                <Input readOnly />
                            </FormItem>
                        </Col>
                        <Col span={6}>
                            <FormItem label="BOM Name" field="bomName">
                                <Input readOnly />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem label="Production Date" field="productionDate">
                                <Input readOnly />
                            </FormItem>
                        </Col>
                        <Col span={6}>
                            <FormItem label="WO Qty" field="woQuantity">
                                <InputNumber readOnly />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row gutter={8}>
                        <Col span={8}>
                            <FormItem label="Warehouse" field="warehouseId" required rules={[{ required: true }]}>
                                <WarehouseSelect onChange={onWarehouseChange} />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem label="Pick Date" field="pickDate">
                                <DatePicker style={{ width: "100%" }} />
                            </FormItem>
                        </Col>
                        <Col span={8}>
                            <FormItem label="Pick Qty">
                                <InputNumber value={pickQtyTotal} precision={0} min={0} onChange={(val) => {
                                    setPickQtyTotal(val || 0);
                                    distributePickQty(val || 0);
                                }} />
                            </FormItem>
                        </Col>
                    </Row>
                    <Row gutter={8}>
                        <Col span={24}>
                            <FormItem label="Notes" field="notes" labelCol={{ span: 1 }}>
                                <Input.TextArea />
                            </FormItem>
                        </Col>
                    </Row>
                </FormItem>
                <FormItem label="Raw Items" labelCol={{ span: 2 }}>
                    <Form.List field="items">
                        {(fields) =>
                            <>
                                <table width="100%">
                                    <thead>
                                        <tr>
                                            <th width="80">Item Number</th>
                                            <th>Item Name</th>
                                            <th>Description</th>
                                            <th width="120">Pick Qty</th>
                                            <th width="60">UOM</th>
                                            <th width="300">Location|Lot#|Expiration</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fields.map((item, index) => {
                                            return (
                                                <tr key={index}>
                                                    <td>
                                                        <FormItem field={item.field + ".id"} hidden><Input /></FormItem>
                                                        <FormItem field={item.field + ".itemId"} hidden><Input /></FormItem>
                                                        <FormItem field={item.field + ".itemName"} hidden><Input /></FormItem>
                                                        <FormItem field={item.field + ".bomQty"} hidden><InputNumber /></FormItem>
                                                        <FormItem field={item.field + ".locationId"} hidden><Input /></FormItem>
                                                        <FormItem field={item.field + ".locationName"} hidden><Input /></FormItem>
                                                        <FormItem field={item.field + ".lot"} hidden><Input /></FormItem>
                                                        <FormItem field={item.field + ".expiredDate"} hidden><Input /></FormItem>
                                                        <FormItem field={item.field + ".itemNo"} noStyle>
                                                            <Input readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".woRawItemId"} noStyle>
                                                            <Select options={items}
                                                                showSearch
                                                                filterOption={(inputValue, option) => option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0}
                                                                triggerProps={{
                                                                    autoAlignPopupWidth: false,
                                                                    autoAlignPopupMinWidth: true
                                                                }}
                                                                onChange={(val) => {
                                                                    const rawItem = _.find(woRawItems, o => o.id === val);
                                                                    const bomRawItem = _.find(bomRawItems, (ri) => ri.itemId === rawItem.itemId);
                                                                    form.setFieldValue(`${item.field}.itemId`, rawItem.itemId);
                                                                    form.setFieldValue(`${item.field}.itemNo`, rawItem.itemNo);
                                                                    form.setFieldValue(`${item.field}.itemName`, rawItem.itemName);
                                                                    form.setFieldValue(`${item.field}.description`, rawItem.description);
                                                                    form.setFieldValue(`${item.field}.bomQty`, bomRawItem ? bomRawItem.quantity : 0);
                                                                    form.setFieldValue(`${item.field}.uom`, rawItem.uom);
                                                                    form.setFieldValue(`${item.field}.pickQty`, null);
                                                                    form.setFieldValue(`${item.field}.locationId`, null);
                                                                    form.setFieldValue(`${item.field}.locationName`, null);
                                                                    form.setFieldValue(`${item.field}.lot`, null);
                                                                    form.setFieldValue(`${item.field}.inventoryKey`, null);
                                                                    form.setFieldValue(`${item.field}.expiredDate`, null);
                                                                }} />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".description"} noStyle>
                                                            <Input readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".pickQty"} noStyle>
                                                            <InputNumber readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".uom"} noStyle>
                                                            <Input readOnly />
                                                        </FormItem>
                                                    </td>
                                                    <td>
                                                        <FormItem field={item.field + ".inventoryKey"} noStyle>
                                                            <InventorySelect
                                                                warehouseId={warehouseId}
                                                                getItemId={() => {
                                                                    return form.getFieldValue(`${item.field}.itemId`);
                                                                }}
                                                                getPickQty={() => {
                                                                    return form.getFieldValue(`${item.field}.pickQty`);
                                                                }}
                                                                onSelect={(val) => onLotSelect(item.field, val)} />
                                                        </FormItem>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </>
                        }
                    </Form.List>
                </FormItem>
            </Form>
        </Card>
    )
}
